/**
 * Pseudonymisering for tall-agenten (VIB-2992).
 *
 * Alt verktøyene sender til modellen går gjennom `scrub`, som bytter ansattes
 * navn med «Ansatt N». Nøkkelen blir i nettleseren, og `restore` bytter
 * tilbake i svaret. Fullt navn byttes først, deretter fornavn som bare én
 * person har, så «Erik» ikke blir stående når «Erik Hansen» er byttet.
 */

export interface Person {
  id: string;
  name: string;
}

export interface Pseudonymizer {
  alias: (id: string) => string;
  scrub: (text: string) => string;
  restore: (text: string) => string;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Ordgrense som også virker for æ, ø og å.
const word = (s: string) => new RegExp(`(?<![\\p{L}\\p{N}])${escape(s)}(?![\\p{L}\\p{N}])`, 'gu');

export function createPseudonymizer(people: Person[]): Pseudonymizer {
  const named = [...new Map(people.filter((p) => p.name.trim()).map((p) => [p.id, p.name.trim()])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], 'nb'));
  const aliasById = new Map(named.map(([id], i) => [id, `Ansatt ${i + 1}`]));
  const nameByAlias = new Map(named.map(([id, name]) => [aliasById.get(id)!, name]));

  const firstNameCount = new Map<string, number>();
  for (const [, name] of named) {
    const first = name.split(/\s+/)[0];
    firstNameCount.set(first, (firstNameCount.get(first) ?? 0) + 1);
  }
  const replacements: Array<[RegExp, string]> = [
    // Lengste navn først, så «Anne Marie Berg» byttes før «Anne Marie».
    ...[...named].sort((a, b) => b[1].length - a[1].length).map(([id, name]): [RegExp, string] => [word(name), aliasById.get(id)!]),
    ...named
      .map(([id, name]) => [id, name.split(/\s+/)[0]] as const)
      .filter(([, first]) => first.length >= 3 && firstNameCount.get(first) === 1)
      .map(([id, first]): [RegExp, string] => [word(first), aliasById.get(id)!]),
  ];

  return {
    alias: (id) => aliasById.get(id) ?? 'Ukjent ansatt',
    scrub: (text) => replacements.reduce((t, [re, alias]) => t.replace(re, alias), text),
    restore: (text) => text.replace(/Ansatt (\d+)(?!\d)/g, (m) => nameByAlias.get(m) ?? m),
  };
}
